<script setup lang="ts">
import type {
  ColumnFiltersState,
} from '@tanstack/vue-table'
import {
  FlexRender,
} from '@tanstack/vue-table'
import Button from '../../ui/button/Button.vue'
import FlatTable from './FlatTable/index.vue'
import TableRowAccordion from './TableRowAccordion/index.vue'
import TableRowAccordionContent from './TableRowAccordionContent/index.vue'
import TableAccordeonTrigger from './TableAccordeonTrigger/index.vue'
import type { ViewActiveMapped } from './composables/useTableLibrary'
import { useTableLibrary } from './composables/useTableLibrary'
import LibraryFilters from './LibraryFilters/index.vue'
import { useLibraryTableInstance } from './composables/useTableInstance'
import { COLUMNS_DEFINITION } from './consts/columnDefinition'
import TableRowSkeleton from './TableRowSkeleton/index.vue'
import TableHeaderSkeleton from './TableHeaderSkeleton/index.vue'
import GroupedActions from './components/GroupedActions/index.vue'
import IconBox from '~~/src/components/DrawerSidebarRight/components/shared/IconBox.vue'
import ElemetalIconPicker from '~~/src/components/Elemental/ElemetalIconPicker/index.vue'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'
import type { Elemental } from '~~/src/types/elemental'
import type { ColumnList } from '~~/src/types/library'
import { ElementalColors } from '~~/src/composables/theme'
import type { GetUserPromptMode } from '~~/src/api/users'
import { useCreateElemental } from '~~/src/composables/mutations/elemetal/useCreateElemental'

interface Props {
  separatedByType?: boolean
  filterByMe?: boolean
  savedMode?: boolean
  hideFilters?: boolean
  hideTableHeader?: boolean
  elementals?: Elemental[]
}

const props = defineProps<Props>()

const separatedByType = ref(props.separatedByType ?? false)
const libraryMode = ref<GetUserPromptMode>('library')
const filters = ref<ColumnFiltersState>([])
const filterByMeEnabled = ref(false)
const columnsDefinition = ref(COLUMNS_DEFINITION)

watch(() => props.separatedByType, (newVal) => {
  separatedByType.value = newVal ?? false
})

const { state } = useElementalListsStore()
const { firebaseUser } = useFirebaseAuth()
const { mutate: createElemental, isPending } = useCreateElemental()
const user = useUser()
const router = useRouter()
const viewsActives = computed(() => state.viewsActives)

const {
  viewActivesMapped,
  isLoading,
  allFeedData,
  viewActivesData,
} = useTableLibrary(viewsActives, firebaseUser, libraryMode, filters)

const currentData: Ref<Elemental[] | ColumnList[] | ViewActiveMapped> = computed(() => {
  if (props.elementals)
    return props.elementals

  return separatedByType.value ? viewActivesMapped.value : allFeedData.value
})

const {
  table,
  headerGroups,
  columns,
  updatePageSize,
} = useLibraryTableInstance(currentData, columnsDefinition, separatedByType)

watch([currentData, separatedByType], () => {
  updatePageSize()
})

const toggleSeparatedByType = () => {
  separatedByType.value = !separatedByType.value
}

const applyFilterByMe = (enabled: boolean) => {
  filterByMeEnabled.value = enabled

  const userId = firebaseUser.value?.uid
  if (!userId)
    return

  const userFilter: ColumnFiltersState = enabled
    ? [{ id: 'user.id', value: userId }]
    : []

  filters.value = userFilter
}

watch(separatedByType, () => {
  if (!filterByMeEnabled.value)
    return

  const userId = firebaseUser.value?.uid
  if (!userId)
    return

  const userFilter: ColumnFiltersState = [{ id: 'user.id', value: userId }]
  filters.value = userFilter
})

watch(() => props.filterByMe, () => {
  applyFilterByMe(props.filterByMe)
})

const toggleLibraryMode = (savedMode: boolean) => {
  libraryMode.value = savedMode ? 'saved' : 'library'
}

onMounted(() => {
  viewActivesData.value.forEach((feed) => {
    feed.query?.refetch?.()
  })
  updatePageSize()
})

const handlerCreateElemental = (elementalTypeId: ElementalTypeEnum, label: string) => {
  const route = getElementalTypeRoute(elementalTypeId)
  if (!user.value)
    return router.push(useGetLoginURL())
  const elementalData = elementalFactory(elementalTypeId, {
    title: `Untitled ${label}`,
  })

  createElemental({ elementalType: elementalTypeId, elemental: elementalData }, {
    onSuccess: (result) => {
      router.push(`/${route}/${result?.id}/edit`)
    },
  })
}

const initialLoad = ref(true)

onMounted(() => {
  setTimeout(() => {
    initialLoad.value = false
  }, 350)
})
</script>

<template>
  <div>
    <LibraryFilters
      :all-feed-data="allFeedData"
      :separated-by-type="separatedByType"
      :hide-filters="hideFilters || false"
      :columns="table.getAllColumns()"
      @update:separated-by-type="() => toggleSeparatedByType()"
      @update:filter-by-me="applyFilterByMe"
      @update:saved-mode="toggleLibraryMode"
    />
    <TransitionSoftSlide direction="down">
      <Table
        v-if="!isLoading || !initialLoad"
        class="border-none"
      >
        <!-- Header -->
        <TableHeader v-if="!separatedByType && !hideTableHeader && allFeedData?.length > 0" class="header-row-elemental">
          <TableRow
            v-for="(headerGroup, index) in headerGroups"
            :key="`headerGroup-${headerGroup.id}-${index}`"
            class="row-elemental h-11 border-b border-grayLighter"
          >
            <TableHead class="h-auto elemental-cell hidden p-0" />
            <TableHead
              v-for="header in headerGroup.headers"
              :key="`header-${header.id}`"
              class="h-auto elemental-cell p-0"
              :class="header.column.id"
            >
              <FlexRender v-if="!header.isPlaceholder" class="h-auto" :render="header.column.columnDef.header" :props="header.getContext()" />
            </TableHead>
          </TableRow>
        </TableHeader>

        <!-- Body -->
        <TableBody>
          <template v-if="table.getRowModel().rows?.length">
            <template v-for="(row, index) in table.getRowModel().rows" :key="`row-${row.id}-${index}`">
              <!-- grouped view -->
              <TableRowAccordion
                v-if="separatedByType"
                :list-id="row.original.label"
                :listtitle="row.original.label.toLocaleLowerCase() === 'lists' ? 'Combined Lists' : row.original.label"
                :is-loading="false"
                :elementals-type="row.original.elementalTypeId"
                :colspan="row.getAllCells()?.length"
                :is-static="false"
              >
                <template #header>
                  <TableAccordeonTrigger
                    :title="row.original.label"
                    class="border-grayLighter border-b group"
                  >
                    <template #icon>
                      <IconBox
                        class="h-7 w-7 !p-0"
                        :style="{
                          backgroundColor: ElementalColors?.[row.original.elementalTypeId]?.[1],
                          color: ElementalColors?.[row.original.elementalTypeId]?.[0],
                        }"
                      >
                        <ElemetalIconPicker
                          :type="row.original.elementalTypeId"
                          class="flex-shrink-0"
                        />
                      </IconBox>
                    </template>
                    <template #actions>
                      <GroupedActions
                        class="group-hover:opacity-100"
                        @click.stop
                      >
                        <template #actions>
                          <Button
                            variant="snack-icon"
                            size="icon"
                            icon="sm"
                            :disabled="isPending"
                            class=" text-soft-black font-700 text-xs"
                            @click="handlerCreateElemental(row.original.elementalTypeId, row.original.label)"
                          >
                            <div v-if="!isPending" class="i-material-symbols:add w-5 h-5" />
                            <div v-else class="i-mdi:loading animate-spin w-4 h-4" />
                          </Button>
                        </template>
                      </GroupedActions>
                    </template>
                  </TableAccordeonTrigger>
                </template>

                <template #content>
                  <TableRowAccordionContent
                    :filters="filters"
                    :elemental-type-id="row.original.elementalTypeId"
                    :label="row.original.label"
                    :colspan="row.getAllCells()?.length"
                    :columns="columns"
                    :is-pending="isPending"
                    @create-elemental="handlerCreateElemental"
                  />
                </template>
              </TableRowAccordion>

              <!-- flat view -->
              <FlatTable
                v-else
                :elemental-type-id="row.original.elementalTypeId"
                :label="row.original.label"
                :colspan="row.getAllCells()?.length"
                :columns="columns"
                :filters="filters"
                :list="row.original"
                :data-index="index"
              />
            </template>
          </template>
          <template v-else>
            <div class="text-center space-y-4 p-4">
              <p class="text-gray-500 text-sm">
                You don't have any Item yet.
              </p>

              <Button
                variant="snack-outline"
                icon="sm"
                class="text-soft-black font-bold text-xs"
                :disabled="isPending"
                @click="handlerCreateElemental(1, 'Prompt')"
              >
                <span class="flex items-center justify-center gap-2">
                  <div v-if="!isPending" class="i-material-symbols:add w-5 h-5" />
                  <div v-else class="i-mdi:loading animate-spin w-4 h-4" />
                  Create your first Prompt
                </span>
              </Button>
            </div>
          </template>
        </TableBody>
      </Table>
    </TransitionSoftSlide>
    <!-- Pagination -->
    <div v-if="table.getCanPreviousPage() || table.getCanNextPage()" class="flex items-center justify-end space-x-2 py-4 px-5">
      <div class="flex-1 text-sm text-muted-foreground">
        Page {{ table.getState().pagination.pageIndex + 1 }} of
        {{ table.getPageCount() }}
      </div>
      <div class="space-x-2">
        <Button
          variant="outline"
          size="sm"
          :disabled="!table.getCanPreviousPage()"
          @click="table.previousPage()"
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          :disabled="!table.getCanNextPage()"
          @click="table.nextPage()"
        >
          Next
        </Button>
      </div>
    </div>
    <div
      v-if="isLoading"
      class="rounded-md"
    >
      <TableHeaderSkeleton v-if="!separatedByType" :columns="columns" />
      <TableRowSkeleton v-for="n in 10" :key="`${n}-loader-library-list`" :columns="columns" :folder-level="1" />
    </div>
  </div>
</template>

<style lang="scss">
.wrapperFilter {
  container-type: inline-size;
  container-name: wrapperFilter;
}

.header-row-elemental {
  container-type: inline-size;
  container-name: header-row-elemental;
}

.row-elemental:not(:active):hover,
.drag-preview-row-elemental.row-elemental {
  background-color: #e6f0ff;
}

.header-row-elemental .row-elemental:hover {
  background-color: transparent;
}

.row-elemental {
  display: flex;
  align-items: center;
  padding: 8px 12px 8px 20px;
  gap: 12px;
  overflow: hidden;
  min-height: 48px;
  transition: background-color 0.3s ease;
  container-type: inline-size;
  container-name: row-elemental;

  &:hover {
    .drag-indicator {
      opacity: 1;
    }
  }

  .elemental-cell {
    flex-shrink: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:nth-of-type(3) {
      @container row-elemental (max-width: 500px) {
        display: none;
      }
    }

    &:nth-of-type(4) {
      @container row-elemental (max-width: 580px) {
        display: none;
      }
    }

    &:nth-of-type(5) {
      @container row-elemental (max-width: 650px) {
        display: none;
      }
    }

    &:nth-of-type(6) {
      @container row-elemental (max-width: 750px) {
        display: none;
      }
    }

    &.title {
      flex: 2 1 460px;
      min-width: 120px;
      max-width: 460px;

      @container row-elemental (max-width: 980px) {
        flex: 2 1 200px;
        width: 100%;
      }
    }

    &.user {
      flex: 1 1 120px;
      min-width: 80px;
    }

    &.avatars {
      flex: 0 0 70px;
    }

    &.elementalTypeId {
      flex: 1 1 110px;
    }

    &.updatedAt,
    &.createdAt {
      flex: 1 1 100px;
    }

    &.actions {
      flex: 0 0 auto;
      display: flex;
      gap: 8px;
    }
  }

  .drag-indicator {
    opacity: 0;
    transition: opacity 0.3s ease;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 20px;
    display: flex;
    align-items: center;
    color: #A8B6D8;
    cursor: grab;
  }
}
</style>
